import { papaparse } from '@/plugins/papaparse.js'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import chroma from 'chroma-js'
import moment from 'moment'
import Session from '@/plugins/session'
import Store from '@/store'
import { SET_CACHE } from '@/store/mutation-types'

export function fetchData (configuration) {
  return new Promise(async resolve => {
    let searchParams = new URLSearchParams()
    const params = cloneDeep(configuration.params)
    translateDateString(params)
    Object.keys(params).map(key => {
      searchParams.append(key, params[key])
    })
    let reqString = new URL(`api/v3/reports/${configuration.report}?${searchParams}`, process.env.VUE_APP_API_HOSTNAME).toString()

    // Try to find if the requesting dashboard widget has already cached in Vuex
    const cachedResults = Store.getters['cache/widgets'](reqString)
    const cacheTime = Store.getters['configuration/layout/cacheTime']

    if (cachedResults && moment(get(cachedResults, 'cachedAt')).add(cacheTime, 'hours').isAfter(new Date())) {
      if (get(configuration, 'timeBreakDown', '')) {
        resolve(formatAsChartDatasetByTime(cachedResults.data, configuration))
      } else {
        resolve(formatAsSimpleChartDataset(cachedResults.data, configuration))
      }
    } else {
      papaparse.parse(reqString, {
        header: true,
        worker: true,
        download: true,
        skipEmptyLines: true,
        downloadRequestHeaders: { 'authorization': `Bearer ${await Session.token}` },
        complete: (results) => {
          if (!results) {
            resolve([])
          } else {
            // Cache the whole results object in Vuex
            Store.commit(`cache/${SET_CACHE}`, { type: 'widgets', url: reqString, results: Object.assign(results, { cachedAt: new Date() }) })
          }

          if (get(configuration, 'timeBreakDown', '')) {
            resolve(formatAsChartDatasetByTime(results.data, configuration))
          } else {
            resolve(formatAsSimpleChartDataset(results.data, configuration))
          }
        }
      })
    }
  })
}

function formatAsChartDatasetByTime (rawData, configuration) {
  let datasets = []
  const groupedDataByTime = groupBy(rawData, configuration.timeBreakDown)
  let allXkeys = []

  if (configuration.timeBreakDown === 'Day') {
    allXkeys = moment.weekdays()
  } else if (configuration.timeBreakDown === 'Date') {
    allXkeys = Object.keys(groupedDataByTime).sort((a, b) => moment(a) - moment(b))
  } else {
    allXkeys = Object.keys(groupedDataByTime)
  }

  rawData.map(raw => {
    const existedDataset = datasets.find(dataset => dataset.label === raw[configuration.xKey])
    if (existedDataset) {
      const dayIndex = allXkeys.findIndex(x => x.toUpperCase() === raw[configuration.timeBreakDown].toUpperCase())
      existedDataset.data[dayIndex] += raw[configuration.yKey] ? Number(raw[configuration.yKey].replace(/[^0-9.-]+/g, '')) : 0
      existedDataset.data[dayIndex] = round(existedDataset.data[dayIndex], 2)
    } else {
      const dataset = {
        label: raw[configuration.xKey],
        data: [],
        type: configuration.chartType.toLowerCase()
      }
      allXkeys.map(() => dataset.data.push(0))
      const dayIndex = allXkeys.findIndex(x => x.toUpperCase() === raw[configuration.timeBreakDown].toUpperCase())
      dataset.data[dayIndex] += raw[configuration.yKey] ? Number(raw[configuration.yKey].replace(/[^0-9.-]+/g, '')) : 0
      dataset.data[dayIndex] = round(dataset.data[dayIndex], 2)
      datasets.push(dataset)
    }
  })

  configuration.labels = []
  let formatted = allXkeys
  if (configuration.timeBreakDown === 'Week') {
    formatted = Object.keys(groupedDataByTime).map(gt => moment(gt, 'YYYYWW').format('[Week] WW, [\']YY'))
  }
  configuration.labels.push(...formatted)

  if (get(configuration, 'top', 0) !== 0 && datasets.length > configuration.top) {
    datasets = groupStackedDatasetsByTopNumber(datasets, configuration)
  }

  // Set dataset label if is undefined, should only apply to comparison widget
  datasets.map(dataset => {
    if (dataset.label === undefined) {
      dataset.label = configuration.yKey
    }
  })

  return datasets
}

function formatAsSimpleChartDataset (rawData, configuration) {
  const groupedData = groupBy(rawData, configuration.xKey)
  let dataset = {
    label: configuration.yKey,
    data: [],
    type: configuration.chartType.toLowerCase()
  }
  configuration.labels = []
  Object.keys(groupedData).map(key => {
    let yValueSum = groupedData[key].map(item => Number(item[configuration.yKey].replace(/[^0-9.-]+/g, ''))).reduce((total, num) => total + num)
    yValueSum = round(yValueSum, 2)
    dataset.data.push(yValueSum)
    configuration.labels.push(key)
  })

  if (get(configuration, 'top', 0) !== 0 && dataset.data.length > configuration.top) {
    groupSimpleDatasetsByTopNumber([dataset], configuration)
  }

  return [dataset]
}

function groupSimpleDatasetsByTopNumber (datasets, configuration) {
  datasets.map(dataset => {
    // Group data to others
    const groupedData = [...dataset.data.slice(0, configuration.top)]
    let sumOtherValue = dataset.data.slice(configuration.top).reduce((total, num) => total + Number(num) || 0, 0)
    if (sumOtherValue) {
      sumOtherValue = round(sumOtherValue, 2)
      groupedData.push(sumOtherValue)
    }
    dataset.data = groupedData
  })
  // Group Labels to Others
  const groupedLabels = [...configuration.labels.slice(0, configuration.top)]
  groupedLabels.push('Other')
  configuration.labels = groupedLabels
}

function groupStackedDatasetsByTopNumber (datasets, configuration) {
  const tops = datasets.slice(0, configuration.top)
  const others = datasets.slice(configuration.top)
  let otherData = []
  configuration.labels.map(() => otherData.push(0))
  others.map(other => {
    other.data.map((d, i) => {
      otherData[i] += d
    })
  })
  otherData = otherData.map(od => round(od, 2))
  tops.push({
    label: 'Other',
    data: otherData,
    type: configuration.chartType.toLowerCase()
  })
  return tops
}

function groupBy (xs, key) {
  return xs.reduce(function (rv, x) {
    if (x[key] !== undefined && x[key] !== '') {
      (rv[x[key].toUpperCase()] = rv[x[key].toUpperCase()] || []).push(x)
    }
    return rv
  }, {})
}

function round (value, decimals) {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals)
}

function translateDateString (params) {
  Object.keys(params).map(key => {
    if (key === 'dateFrom') {
      switch (params[key]) {
        case 'week': {
          params[key] = moment().startOf('isoweek').format('YYYY-MM-DD')
          break
        }
        case 'month': {
          params[key] = moment().startOf('month').format('YYYY-MM-DD')
          break
        }
        case 'year': {
          params[key] = moment().startOf('year').format('YYYY-MM-DD')
          break
        }
      }
    } else if (key === 'dateTo') {
      switch (params[key]) {
        case 'today': {
          params[key] = moment(new Date()).format('YYYY-MM-DD')
          break
        }
      }
    }
  })
}

export function setChartDataBackgroundColours (chartData) {
  if (get(chartData, 'datasets[0].type', '').toLowerCase() === 'pie') {
    chartData.datasets.map(dataset => {
      dataset.backgroundColor = chroma.scale(['#fafa6e', '#2A4858']).mode('lch').colors(chartData.labels.length)
    })
  } else {
    const colours = chroma.scale(['#fafa6e', '#2A4858']).mode('lch').colors(chartData.datasets.length)
    chartData.datasets.map((dataset, index) => {
      dataset.backgroundColor = colours[index]
    })
  }
  return chartData
}

export function setOptionsForChartType (chartData) {
  chartData.datasets.map(dataset => {
    switch (dataset.type.toLowerCase()) {
      case 'line': {
        setLineChartOptions(dataset)
        break
      }
      case 'area': {
        setAreaChartOptions(dataset)
        break
      }
    }
  })
  return chartData
}

function setLineChartOptions (dataset) {
  dataset.fill = false
  dataset.borderColor = dataset.backgroundColor
}

function setAreaChartOptions (dataset) {
  dataset.type = 'line'
}
